/**
 * CopilotWidget — floating companion rendered in a portal on document.body.
 *
 *  - Minimized: round floating bubble (bottom-right) with pending badge.
 *  - Open: 380x560 window, draggable by its title bar (hand-rolled pointer
 *    events, no lib), messages, proposed-action cards (Confirmer/Rejeter),
 *    collapsible execution timeline, input (Enter to send) and a
 *    backend-availability indicator. Dark slate styling, works on any page.
 */

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { getBalance } from '@/lib/api/credits';
import { ModelPicker, WithCreditCost } from '@/features/credits';
import { useCopilotStore } from '@/stores/copilot-store';
import type { PlayerPace, PlayerStatus, PlayerStep, PlayerStepStatus } from '@/stores/copilot-store';
import { useAdvancedMode } from '@/components/AdvancedMode';
import { READ_ONLY_HINT, useRoles } from '@/routes/settings/useRoles';

import { getCurrent } from './context-registry';
import { uploadCopilotFile } from './file-upload';
import { RichResponse, type RichAction } from './rich/RichResponse';
import { useSpeech } from './useSpeech';
import { VoiceRecorder } from './VoiceRecorder';
import type { CopilotAction, CopilotActionType, CopilotMessage, ExecutionStatus } from './types';
import { useT, tr, intlLocale } from '@/i18n';

// Opt-in auto-read toggle persistence. Wrapped in try/catch — localStorage can
// throw (private mode / blocked storage) and must never break the widget.
const AUTO_READ_STORAGE_KEY = 'copilot:autoReadReplies';

function loadAutoRead(): boolean {
  try {
    return window.localStorage.getItem(AUTO_READ_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistAutoRead(value: boolean): void {
  try {
    window.localStorage.setItem(AUTO_READ_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore — the toggle still works for this session */
  }
}

const ACTION_LABELS: Record<CopilotActionType, string> = {
  navigate: tr('shell.features.copilotCopilotWidget.navigation'),
  open_form: tr('shell.features.copilotCopilotWidget.openAForm'),
  fill_field: tr('shell.features.copilotCopilotWidget.fillInAField'),
  fill_form: tr('shell.features.copilotCopilotWidget.fillInAForm'),
  submit_form: tr('shell.features.copilotCopilotWidget.submitAForm'),
  select_table_rows: tr('shell.features.copilotCopilotWidget.selectRows'),
  highlight: tr('shell.features.copilotCopilotWidget.highlight'),
  open_modal: tr('shell.features.copilotCopilotWidget.openAWindow'),
  scroll_to: tr('shell.features.copilotCopilotWidget.scrollTo'),
  toast: tr('shell.features.copilotCopilotWidget.notification'),
  import_records: tr('shell.features.copilotCopilotWidget.importRecords'),
  wait_user_confirm: tr('shell.features.copilotCopilotWidget.waitForAConfirmation'),
  execute_tool: tr('shell.features.copilotCopilotWidget.runAnAction'),
};

const STATUS_STYLES: Record<ExecutionStatus, { dot: string; label: string }> = {
  executed: { dot: 'bg-emerald-400', label: tr('shell.features.copilotCopilotWidget.executed') },
  failed: { dot: 'bg-rose-400', label: tr('shell.features.copilotCopilotWidget.failed') },
  skipped: { dot: 'bg-slate-400', label: tr('shell.features.copilotCopilotWidget.ignored') },
};

// Paced player — labels/styles for the opt-in step-by-step "watch & intervene"
// mode (see copilot-store.ts `runPlayerLoop`). Purely presentational; the
// player's state machine lives entirely in the store.
const PLAYER_STATUS_LABELS: Record<PlayerStatus, string> = {
  idle: tr('shell.features.copilotCopilotWidget.ready'),
  playing: tr('shell.features.copilotCopilotWidget.reading'),
  paused: tr('shell.features.copilotCopilotWidget.paused'),
  waiting: tr('shell.features.copilotCopilotWidget.awaitingYourConfirmation'),
  done: tr('shell.features.copilotCopilotWidget.done'),
};

const PLAYER_STEP_STYLES: Record<PlayerStepStatus, { dot: string; label: string }> = {
  pending: { dot: 'bg-slate-500', label: tr('shell.features.copilotCopilotWidget.pending') },
  running: { dot: 'bg-indigo-400 animate-pulse', label: tr('shell.features.copilotCopilotWidget.inProgress') },
  waiting: { dot: 'bg-amber-400', label: tr('shell.features.copilotCopilotWidget.pausedConfirmation') },
  executed: STATUS_STYLES.executed,
  failed: STATUS_STYLES.failed,
  skipped: STATUS_STYLES.skipped,
};

function clampPos(p: { x: number; y: number }): { x: number; y: number } {
  if (typeof window === 'undefined') return p;
  return {
    x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - 100)),
    y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - 56)),
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(intlLocale('fr-FR'), {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
}

function summarizeTarget(action: CopilotAction): string {
  const t = action.target;
  if (!t) return '';
  const parts: string[] = [];
  if (t.tool) parts.push(`outil : ${t.tool}`);
  if (t.route) parts.push(`route : ${t.route}`);
  if (t.page_id) parts.push(`page : ${t.page_id}`);
  if (t.form_id) parts.push(`formulaire : ${t.form_id}`);
  if (t.field) parts.push(`champ : ${t.field}`);
  return parts.join(' · ');
}

/**
 * Turn a RichResponse form submit into a structured, machine-actionable user
 * turn. There is no direct "submit these form values" copilot endpoint — the
 * confirm gate (`/actions/{id}/confirm`) + `_SUBMIT_FORM_BRIDGE` / tool call
 * only fire on an action the backend PROPOSED during a message turn. So a
 * `form` block's submit is fed back as a new turn carrying the `formId`
 * (its `_SUBMIT_FORM_BRIDGE` key), the backend `tool`, and the entered values;
 * the backend then proposes the real `submit_form`/`execute_tool` action, which
 * flows through the exact same normalize → pending/agent-autorun →
 * `runRegisteredAction` → CASL/HIL-gated persistence path as every other
 * Action Protocol submit. Nothing here bypasses that gate.
 */
function describeRichSubmit(action: RichAction): string {
  const entries = Object.entries(action.values ?? {});
  const rendered =
    entries.length > 0
      ? entries
          .map(([key, value]) => {
            const shown =
              typeof value === 'boolean'
                ? value
                  ? tr('shell.features.copilotCopilotWidget.yes')
                  : tr('shell.features.copilotCopilotWidget.no')
                : String(value);
            return `- ${key} : ${shown}`;
          })
          .join('\n')
      : tr('shell.features.copilotCopilotWidget.noValueEntered');
  const head = action.formId
    ? tr('shell.features.copilotCopilotWidget.submitTheFormidForm', { formId: action.formId })
    : action.tool
      ? tr('shell.features.copilotCopilotWidget.runTheToolAction', { tool: action.tool })
      : tr('shell.features.copilotCopilotWidget.submitTheForm');
  const toolNote = action.tool && action.formId ? ` (outil : ${action.tool})` : '';
  return tr('shell.features.copilotCopilotWidget.headToolnoteWithTheFollowingValues', { head, toolNote, rendered });
}

function MessageBubble({
  message,
  onRichAction,
  speechSupported,
  isSpeaking,
  onToggleSpeak,
}: {
  message: CopilotMessage;
  onRichAction: (action: RichAction) => void;
  /** True when Web Speech TTS is available — gates the "Lire" control. */
  speechSupported: boolean;
  /** True when THIS message is the one currently being spoken aloud. */
  isSpeaking: boolean;
  /** Toggle speaking this assistant message aloud. */
  onToggleSpeak: (message: CopilotMessage) => void;
}) {
  const { t: tx } = useT();
  if (message.role === 'system') {
    return <p className="text-center text-[11px] italic text-slate-500">{message.content}</p>;
  }
  const isUser = message.role === 'user';
  const hasView = !isUser && Array.isArray(message.view) && message.view.length > 0;
  const hasBubble = message.content !== '' || (message.actions && message.actions.length > 0);
  // Spoken output only applies to ASSISTANT text (not user turns, not the
  // system notices handled above), and only when there is text to read.
  const canSpeak = !isUser && speechSupported && message.content.trim() !== '';
  return (
    <div className={`flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
      {hasBubble && (
        <div
          className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-sm ${
            isUser ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-100'
          }`}
        >
          {message.content}
          {message.actions && message.actions.length > 0 && (
            <p className={`mt-1 text-[10px] ${isUser ? 'text-indigo-200' : 'text-slate-400'}`}>
              {message.actions.length}{' '}{tx('shell.features.copilotCopilotWidget.proposedActionS')}</p>
          )}
        </div>
      )}
      {canSpeak && (
        <button
          type="button"
          onClick={() => onToggleSpeak(message)}
          aria-label={isSpeaking ? tx('shell.features.copilotCopilotWidget.stopReadingThisAnswer') : tx('shell.features.copilotCopilotWidget.readThisAnswerAloud')}
          aria-pressed={isSpeaking}
          title={isSpeaking ? tx('shell.features.copilotCopilotWidget.stopReading') : tx('shell.features.copilotCopilotWidget.readTheAnswerAloud')}
          className={`self-start rounded px-1.5 py-0.5 text-[10px] ${
            isSpeaking
              ? 'bg-rose-500/20 text-rose-300 hover:bg-rose-500/30'
              : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
          }`}
        >
          {isSpeaking ? tx('shell.features.copilotCopilotWidget.stop') : tx('shell.features.copilotCopilotWidget.read')}
        </button>
      )}
      {hasView && (
        <div className="w-full max-w-full">
          <RichResponse view={message.view} onAction={onRichAction} />
        </div>
      )}
    </div>
  );
}

function ActionCard({
  action,
  onConfirm,
  onReject,
}: {
  action: CopilotAction;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const { t: tx } = useT();
  const label = ACTION_LABELS[action.type] ?? action.type;
  const target = summarizeTarget(action);
  return (
    <div className="rounded-lg border border-slate-600 bg-slate-800 p-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-100">{label}</span>
        {action.preview && (
          <span className="rounded border border-amber-500/50 px-1 text-[10px] text-amber-300">{tx('shell.features.copilotCopilotWidget.preview')}</span>
        )}
      </div>
      {target && <p className="mt-0.5 truncate text-[11px] text-slate-400">{target}</p>}
      <div className="mt-1.5 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          aria-label={tx('shell.features.copilotCopilotWidget.confirmTheActionLabel', { label })}
          className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-500"
        >
          {action.requires_confirmation ? tx('shell.features.copilotCopilotWidget.confirm') : tx('shell.features.copilotCopilotWidget.run')}
        </button>
        <button
          type="button"
          onClick={onReject}
          aria-label={tx('shell.features.copilotCopilotWidget.rejectTheActionLabel', { label })}
          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
        >{tx('shell.features.copilotCopilotWidget.reject')}</button>
      </div>
    </div>
  );
}

/** One row of the paced player's step queue — highlights the current step. */
function PlayerStepRow({
  step,
  index,
  current,
}: {
  step: PlayerStep;
  index: number;
  current: boolean;
}) {
  const style = PLAYER_STEP_STYLES[step.status];
  const label = ACTION_LABELS[step.action.type] ?? step.action.type;
  const target = summarizeTarget(step.action);
  return (
    <li
      aria-current={current ? 'step' : undefined}
      className={`flex items-start gap-2 rounded px-1.5 py-1 ${
        current ? 'bg-indigo-500/15 ring-1 ring-inset ring-indigo-400' : ''
      }`}
    >
      <span
        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
        title={style.label}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-slate-200">
          {index + 1}. {label}
        </span>
        {target && <span className="block truncate text-[10px] text-slate-500">{target}</span>}
        {step.detail && (
          <span className="block truncate text-[10px] text-slate-500">{step.detail}</span>
        )}
      </span>
    </li>
  );
}

export function CopilotWidget() {
  const { t: tx } = useT();
  // S5 (security I0): every copilot message consumes AI credits — a write that
  // the server refuses (403) to read-only viewer/client accounts.
  const { isWriter } = useRoles();
  const {
    open,
    minimized,
    position,
    mode,
    messages,
    pendingActions,
    timeline,
    toasts,
    backendAvailable,
    sending,
    model,
    temperature,
    maxSteps,
    playerRunMode,
    playerPace,
    playerStatus,
    playerSteps,
    playerStepIndex,
    playerWaitPrompt,
    openWidget,
    setMinimized,
    setPosition,
    setMode,
    setModel,
    setTemperature,
    setMaxSteps,
    sendMessage,
    confirmAction,
    rejectAction,
    pushToast,
    dismissToast,
    setPlayerRunMode,
    setPlayerPace,
    playerPlay,
    playerPause,
    playerStepOnce,
    playerStop,
    playerContinueWait,
    playerIntervene,
  } = useCopilotStore();

  const { advanced: copilotAdvanced, toggle: toggleCopilotAdvanced } = useAdvancedMode('copilot');

  // Browser-native text-to-speech (no backend). `supported` gates all controls.
  const speech = useSpeech();
  const {
    supported: speechSupported,
    speaking: speechSpeaking,
    speak: speakText,
    stop: stopSpeech,
  } = speech;
  const [autoRead, setAutoRead] = useState<boolean>(loadAutoRead);
  // Id of the message currently being spoken (drives the per-bubble Lire/Stop toggle).
  const [spokenId, setSpokenId] = useState<string | null>(null);
  // Tracks the latest assistant message already handled by auto-read, so a toggle
  // flipped ON reads only genuinely NEW replies, never the existing backlog.
  const autoReadSeenRef = useRef<string | null>(null);
  const autoReadInitRef = useRef(false);

  const [draft, setDraft] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [livePos, setLivePos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const lastDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composeInputRef = useRef<HTMLInputElement>(null);

  const pos = clampPos(livePos ?? position);
  const canAttach = backendAvailable && !sending;

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  // Credit-balance transparency: fetch the tenant's wallet whenever the
  // widget is opened, and refresh it right after a turn (which consumes
  // credits server-side). Best-effort only — a failure just hides the
  // indicator, it must never break the widget.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getBalance()
      .then((wallet) => {
        if (!cancelled) setCreditBalance(wallet.available);
      })
      .catch(() => {
        if (!cancelled) setCreditBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const refreshBalance = () =>
    void getBalance()
      .then((wallet) => setCreditBalance(wallet.available))
      .catch(() => {
        /* keep last known balance on refresh failure */
      });

  // Keep the per-bubble toggle in sync: once speech ends (naturally or via
  // stop/cancel), clear the "currently spoken" highlight.
  useEffect(() => {
    if (!speechSpeaking) setSpokenId(null);
  }, [speechSpeaking]);

  // Stop speaking when the copilot is closed or minimized (hidden from view).
  useEffect(() => {
    if (!open || minimized) stopSpeech();
  }, [open, minimized, stopSpeech]);

  // Auto-read: speak each NEW assistant reply once when the toggle is ON. The
  // seen-ref advances on every change (even when OFF) so flipping it ON never
  // replays older messages; the first pass only records the current tail.
  useEffect(() => {
    let lastAssistant: CopilotMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') {
        lastAssistant = messages[i];
        break;
      }
    }
    if (!lastAssistant) return;
    if (!autoReadInitRef.current) {
      autoReadInitRef.current = true;
      autoReadSeenRef.current = lastAssistant.id;
      return;
    }
    if (lastAssistant.id === autoReadSeenRef.current) return;
    autoReadSeenRef.current = lastAssistant.id;
    if (autoRead && speechSupported && lastAssistant.content.trim() !== '') {
      speakText(lastAssistant.content);
      setSpokenId(lastAssistant.id);
    }
  }, [messages, autoRead, speechSupported, speakText]);

  const toggleAutoRead = () => {
    const next = !autoRead;
    setAutoRead(next);
    persistAutoRead(next);
    if (!next) stopSpeech();
  };

  const toggleSpeakMessage = (message: CopilotMessage) => {
    if (spokenId === message.id && speechSpeaking) {
      stopSpeech();
      return;
    }
    speakText(message.content);
    setSpokenId(message.id);
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    stopSpeech(); // a new user turn stops any in-progress spoken reply
    setDraft('');
    void sendMessage(text, getCurrent()).then(refreshBalance);
  };

  // "Intervenir" — the paced-player affordance: pauses an active playing run
  // BEFORE sending (via `playerIntervene`) so the intervention and the
  // auto-advancing queue never race on screen; a run already 'waiting' on a
  // wait_user_confirm step stays 'waiting' (still resolved via Continuer).
  const handleIntervene = () => {
    const text = draft.trim();
    if (!text || sending) return;
    stopSpeech(); // a new user turn stops any in-progress spoken reply
    setDraft('');
    void playerIntervene(text, getCurrent()).then(refreshBalance);
  };

  // "Modifier" / "Poser une question" on a wait_user_confirm pause: both hand
  // control back to the compose input (Intervenir remains the explicit way to
  // send) rather than auto-advancing the run.
  const focusCompose = () => composeInputRef.current?.focus();

  // A rich `form` block submit (RichResponse onAction) is routed through the
  // real Action Protocol submit path: it becomes a structured user turn, the
  // backend proposes the matching submit_form/execute_tool action, and that runs
  // through the same confirm gate + `_SUBMIT_FORM_BRIDGE` / CASL screen as any
  // other copilot action. See `describeRichSubmit`.
  const handleRichAction = (action: RichAction) => {
    if (action.type !== 'submit_form' || sending) return;
    void sendMessage(describeRichSubmit(action), getCurrent()).then(refreshBalance);
  };

  const onFileChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (file) void uploadCopilotFile(file);
  };

  const onDragOver = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!canAttach) return;
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setDragging(true);
    }
  };

  const onDragLeave = (e: ReactDragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>) => {
    setDragging(false);
    if (!canAttach) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      e.preventDefault();
      void uploadCopilotFile(file);
    }
  };

  const onTitlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Do not start a drag from the title-bar buttons.
    if ((e.target as HTMLElement).closest('button')) return;
    dragOffsetRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTitlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const offset = dragOffsetRef.current;
    if (!offset) return;
    const next = clampPos({ x: e.clientX - offset.dx, y: e.clientY - offset.dy });
    lastDragPosRef.current = next;
    setLivePos(next);
  };

  const onTitlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer capture already released
    }
    if (lastDragPosRef.current) {
      setPosition(lastDragPosRef.current);
      lastDragPosRef.current = null;
    }
    setLivePos(null);
  };

  const showWindow = open && !minimized;

  const bubble = (
    <button
      type="button"
      onClick={openWidget}
      aria-label={tx('shell.features.copilotCopilotWidget.openTheCopilot')}
      className="fixed bottom-5 right-5 z-[10001] flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-xl transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 10h8m-8 4h5m-9 6 2.6-2.6A2 2 0 0 1 8 17h9a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v13Z"
        />
      </svg>
      {pendingActions.length > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-slate-900"
          aria-label={tx('shell.features.copilotCopilotWidget.lengthActionSPending', { length: pendingActions.length })}
        >
          {pendingActions.length}
        </span>
      )}
      {!backendAvailable && (
        <span
          className="absolute -left-0.5 -top-0.5 h-3 w-3 rounded-full border border-slate-900 bg-amber-400"
          title={tx('shell.features.copilotCopilotWidget.copilotBackendUnavailable')}
          aria-hidden="true"
        />
      )}
    </button>
  );

  const panel = (
    <div
      role="dialog"
      aria-label={tx('shell.features.copilotCopilotWidget.copilot')}
      className="fixed z-[10001] flex h-[560px] max-h-[calc(100vh-16px)] w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* Title bar — drag handle */}
      <div
        className="flex h-11 shrink-0 cursor-move touch-none select-none items-center gap-2 border-b border-slate-700 bg-slate-800 px-3"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={onTitlePointerUp}
        onPointerCancel={onTitlePointerUp}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            backendAvailable ? 'bg-emerald-400' : 'bg-amber-400'
          }`}
          title={backendAvailable ? tx('shell.features.copilotCopilotWidget.copilotBackendConnected') : tx('shell.features.copilotCopilotWidget.copilotBackendUnavailable')}
          aria-hidden="true"
        />
        <span className="text-sm font-semibold">{tx('shell.features.copilotCopilotWidget.copilot')}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            mode === 'agent' ? 'bg-amber-500/20 text-amber-300' : 'bg-sky-500/20 text-sky-300'
          }`}
        >
          {mode === 'agent' ? tx('shell.features.copilotCopilotWidget.agent') : tx('shell.features.copilotCopilotWidget.assistant')}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {speechSupported && (
            <button
              type="button"
              role="switch"
              aria-checked={autoRead}
              onClick={toggleAutoRead}
              aria-label={tx('shell.features.copilotCopilotWidget.readAnswersAloud')}
              title={
                autoRead
                  ? tx('shell.features.copilotCopilotWidget.readingAnswersAloudEnabledDisable')
                  : tx('shell.features.copilotCopilotWidget.readAnswersAloudAutomatically')
              }
              className={`rounded px-2 py-1 text-[11px] ${
                autoRead ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              <span aria-hidden="true">🔊</span>{' '}{tx('shell.features.copilotCopilotWidget.voice')}</button>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={copilotAdvanced}
            onClick={toggleCopilotAdvanced}
            aria-label={tx('shell.features.copilotCopilotWidget.advancedMode')}
            title={copilotAdvanced ? tx('shell.features.copilotCopilotWidget.hideAdvancedOptions') : tx('shell.features.copilotCopilotWidget.showAdvancedOptions')}
            className={`rounded px-2 py-1 text-[11px] ${
              copilotAdvanced ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >{tx('shell.features.copilotCopilotWidget.advanced')}</button>
          <button
            type="button"
            role="switch"
            aria-checked={playerRunMode === 'paced'}
            onClick={() => setPlayerRunMode(playerRunMode === 'paced' ? 'auto' : 'paced')}
            aria-label={
              playerRunMode === 'paced'
                ? tx('shell.features.copilotCopilotWidget.disableStepByStepMode')
                : tx('shell.features.copilotCopilotWidget.enableStepByStepMode')
            }
            title={tx('shell.features.copilotCopilotWidget.observableExecutionPlayPauseStepBy')}
            className={`rounded px-2 py-1 text-[11px] ${
              playerRunMode === 'paced' ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >{tx('shell.features.copilotCopilotWidget.stepByStep')}</button>
          <button
            type="button"
            onClick={() => setMode(mode === 'agent' ? 'assistant' : 'agent')}
            aria-label={
              mode === 'agent' ? tx('shell.features.copilotCopilotWidget.switchToAssistantMode') : tx('shell.features.copilotCopilotWidget.switchToAgentMode')
            }
            className="rounded px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-700"
          >
            {mode === 'agent' ? tx('shell.features.copilotCopilotWidget.assistantMode') : tx('shell.features.copilotCopilotWidget.agentMode')}
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label={tx('shell.features.copilotCopilotWidget.minimizeTheCopilot')}
            className="rounded px-2 py-1 text-slate-300 hover:bg-slate-700"
          >
            <span aria-hidden="true">—</span>
          </button>
        </div>
      </div>

      {/* Backend availability banner */}
      {!backendAvailable && (
        <div
          role="status"
          className="shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300"
        >{tx('shell.features.copilotCopilotWidget.copilotBackendUnavailableLocalModeSimula')}</div>
      )}

      {/* Advanced options (model / temperature / agent steps) */}
      {copilotAdvanced && (
        <div className="shrink-0 space-y-2 border-b border-slate-700 bg-slate-800/60 px-3 py-2 text-xs text-slate-300">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tx('shell.features.copilotCopilotWidget.advancedOptions')}</p>
          <div className="flex items-center justify-between gap-2">
            <span>{tx('shell.features.copilotCopilotWidget.model')}</span>
            <ModelPicker value={model} onChange={setModel} compact />
          </div>
          <label className="flex items-center justify-between gap-2">
            <span>{tx('shell.features.copilotCopilotWidget.temperatureTemperature', { temperature: temperature.toFixed(1) })}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              aria-label={tx('shell.features.copilotCopilotWidget.temperature')}
              className="w-28 accent-indigo-500"
            />
          </label>
          {mode === 'agent' && (
            <label className="flex items-center justify-between gap-2">
              <span>{tx('shell.features.copilotCopilotWidget.maxStepsAgent')}</span>
              <input
                type="number"
                min={1}
                max={10}
                value={maxSteps}
                onChange={(e) => setMaxSteps(Number(e.target.value))}
                aria-label={tx('shell.features.copilotCopilotWidget.maximumNumberOfAgentSteps')}
                className="w-16 rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-xs text-slate-100"
              />
            </label>
          )}
          <p className="text-[10px] text-slate-500">{tx('shell.features.copilotCopilotWidget.sentToTheCopilotIgnoredAs')}</p>
        </div>
      )}

      {/* Messages */}
      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label={tx('shell.features.copilotCopilotWidget.copilotMessages')}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative flex-1 space-y-2 overflow-y-auto px-3 py-2 ${
          dragging ? 'ring-2 ring-inset ring-indigo-500' : ''
        }`}
      >
        {messages.length === 0 && (
          <p className="pt-8 text-center text-xs text-slate-500">{tx('shell.features.copilotCopilotWidget.askAQuestionDropAFile')}</p>
        )}
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onRichAction={handleRichAction}
            speechSupported={speechSupported}
            isSpeaking={spokenId === message.id && speechSpeaking}
            onToggleSpeak={toggleSpeakMessage}
          />
        ))}
        {sending && <p className="text-xs text-slate-400">{tx('shell.features.copilotCopilotWidget.theCopilotIsThinking')}</p>}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-slate-900/80 text-sm font-medium text-indigo-300">{tx('shell.features.copilotCopilotWidget.dropTheFileToAnalyzeIt')}</div>
        )}
      </div>

      {/* Paced player — opt-in step-by-step "watch & intervene" mode. Only
          meaningful in agent mode: the store only routes actions into the
          player queue when `mode === 'agent'` (assistant mode already puts
          every action on a manual Confirmer/Rejeter card, unchanged). */}
      {playerRunMode === 'paced' && mode === 'agent' && (
        <div className="max-h-56 shrink-0 space-y-2 overflow-y-auto border-t border-slate-700 bg-slate-800/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tx('shell.features.copilotCopilotWidget.stepByStepPlayer')}</p>
            <span role="status" className="text-[10px] text-slate-400">
              {PLAYER_STATUS_LABELS[playerStatus]}
              {playerSteps.length > 0
                ? ` (${Math.min(playerStepIndex + 1, playerSteps.length)}/${playerSteps.length})`
                : ''}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={playerStatus === 'playing' ? playerPause : playerPlay}
              disabled={
                playerStatus === 'waiting' ||
                (playerStatus !== 'playing' && playerStepIndex >= playerSteps.length)
              }
              aria-label={playerStatus === 'playing' ? tx('shell.features.copilotCopilotWidget.pausePlayback') : tx('shell.features.copilotCopilotWidget.startPlayback')}
              className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {playerStatus === 'playing' ? tx('shell.features.copilotCopilotWidget.pause') : tx('shell.features.copilotCopilotWidget.read2')}
            </button>
            <button
              type="button"
              onClick={playerStepOnce}
              disabled={
                playerStatus === 'playing' || playerStatus === 'waiting' || playerStepIndex >= playerSteps.length
              }
              aria-label={tx('shell.features.copilotCopilotWidget.runTheNextStep')}
              className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >{tx('shell.features.copilotCopilotWidget.nextStep')}</button>
            <button
              type="button"
              onClick={playerStop}
              disabled={playerSteps.length === 0}
              aria-label={tx('shell.features.copilotCopilotWidget.stopStepByStepPlayback')}
              className="rounded border border-rose-500/50 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-40"
            >{tx('shell.features.copilotCopilotWidget.stop2')}</button>
            <label className="ml-auto flex items-center gap-1 text-[11px] text-slate-400">
              <span className="sr-only">{tx('shell.features.copilotCopilotWidget.playbackSpeed')}</span>
              <select
                value={playerPace}
                onChange={(e) => setPlayerPace(e.target.value as PlayerPace)}
                aria-label={tx('shell.features.copilotCopilotWidget.copilotPlaybackSpeed')}
                className="rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-[11px] text-slate-100"
              >
                <option value="lent">{tx('shell.features.copilotCopilotWidget.slow')}</option>
                <option value="normal">{tx('shell.features.copilotCopilotWidget.normal')}</option>
                <option value="rapide">{tx('shell.features.copilotCopilotWidget.fast')}</option>
              </select>
            </label>
          </div>

          {playerStatus === 'waiting' && playerWaitPrompt && (
            <div role="alert" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2">
              <p className="text-[11px] text-amber-200">{playerWaitPrompt}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={playerContinueWait}
                  aria-label={tx('shell.features.copilotCopilotWidget.continueExecution')}
                  className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-500"
                >{tx('shell.features.copilotCopilotWidget.continue')}</button>
                <button
                  type="button"
                  onClick={focusCompose}
                  aria-label={tx('shell.features.copilotCopilotWidget.changeWhatComesNextViaA')}
                  className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700"
                >{tx('shell.features.copilotCopilotWidget.edit')}</button>
                <button
                  type="button"
                  onClick={focusCompose}
                  aria-label={tx('shell.features.copilotCopilotWidget.askTheCopilotAQuestion')}
                  className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700"
                >{tx('shell.features.copilotCopilotWidget.askAQuestion')}</button>
              </div>
            </div>
          )}

          {playerSteps.length > 0 && (
            <ul className="space-y-0.5" aria-label={tx('shell.features.copilotCopilotWidget.stepByStepExecutionQueue')}>
              {playerSteps.map((step, index) => (
                <PlayerStepRow
                  key={`${step.action.action_id}-${index}`}
                  step={step}
                  index={index}
                  current={index === playerStepIndex}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Proposed actions */}
      {pendingActions.length > 0 && (
        <div className="max-h-44 shrink-0 space-y-2 overflow-y-auto border-t border-slate-700 bg-slate-800/60 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tx('shell.features.copilotCopilotWidget.proposedActions')}</p>
          {pendingActions.map((action) => (
            <ActionCard
              key={action.action_id}
              action={action}
              onConfirm={() => confirmAction(action.action_id)}
              onReject={() => rejectAction(action.action_id)}
            />
          ))}
        </div>
      )}

      {/* Execution timeline (collapsible) */}
      <div className="shrink-0 border-t border-slate-700">
        <button
          type="button"
          onClick={() => setShowTimeline((v) => !v)}
          aria-expanded={showTimeline}
          aria-label={tx('shell.features.copilotCopilotWidget.showOrHideTheTimelineOf')}
          className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-800"
        >
          <span>{tx('shell.features.copilotCopilotWidget.actionTimelineLength', { length: timeline.length })}</span>
          <span aria-hidden="true">{showTimeline ? '▾' : '▸'}</span>
        </button>
        {showTimeline && (
          <ul className="max-h-32 overflow-y-auto px-3 pb-2 text-xs" aria-label={tx('shell.features.copilotCopilotWidget.executedActions')}>
            {timeline.length === 0 && (
              <li className="text-slate-500">{tx('shell.features.copilotCopilotWidget.noActionExecutedYet')}</li>
            )}
            {[...timeline].reverse().map((entry, index) => {
              const style = STATUS_STYLES[entry.status];
              return (
                <li key={`${entry.at}-${index}`} className="flex items-start gap-2 py-0.5">
                  <span
                    className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
                    title={style.label}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 break-words text-slate-300">
                    {ACTION_LABELS[entry.action.type] ?? entry.action.type}
                    {entry.detail ? ` — ${entry.detail}` : ''}
                  </span>
                  <span className="shrink-0 text-slate-500">{formatTime(entry.at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Credit balance — transparency on remaining AI credits */}
      {creditBalance !== null && (
        <div
          className="flex shrink-0 items-center justify-end border-t border-slate-700 bg-slate-800/40 px-3 py-1 text-[10px] text-slate-400"
          title={tx('shell.features.copilotCopilotWidget.remainingAiCreditBalanceForThis')}
        >
          {Math.round(creditBalance).toLocaleString(intlLocale('fr-FR'))}{' '}{tx('shell.features.copilotCopilotWidget.credit')}{Math.round(creditBalance) === 1 ? '' : 's'}{' '}{tx('shell.features.copilotCopilotWidget.left')}{Math.round(creditBalance) === 1 ? '' : 's'}
        </div>
      )}

      {/* Input */}
      {!isWriter ? (
        <div
          role="note"
          data-testid="copilot-read-only"
          className="shrink-0 border-t border-slate-700 bg-slate-800 p-3 text-xs text-slate-300"
        >
          {READ_ONLY_HINT}.
        </div>
      ) : (
      <div className="flex shrink-0 items-center gap-2 border-t border-slate-700 bg-slate-800 p-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          tabIndex={-1}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttach}
          aria-label={tx('shell.features.copilotCopilotWidget.attachAFile')}
          title={
            backendAvailable
              ? tx('shell.features.copilotCopilotWidget.attachAFileToAnalyze')
              : tx('shell.features.copilotCopilotWidget.fileImportUnavailableCopilotBackendOffli')
          }
          className="shrink-0 rounded-md border border-slate-600 p-1.5 text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
            />
          </svg>
        </button>
        <input
          ref={composeInputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={mode === 'agent' ? tx('shell.features.copilotCopilotWidget.askForAnAction') : tx('shell.features.copilotCopilotWidget.askAQuestion2')}
          aria-label={tx('shell.features.copilotCopilotWidget.messageForTheCopilot')}
          className="min-w-0 flex-1 rounded-md border border-slate-600 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
        />
        <VoiceRecorder
          onTranscript={(text) => {
            const clean = text.trim();
            if (clean) setDraft((prev) => (prev.trim() ? `${prev.trim()} ${clean}` : clean));
          }}
          onError={(msg) => pushToast(msg)}
          language="fr"
          disabled={sending}
          className="shrink-0"
        />
        {playerRunMode === 'paced' && mode === 'agent' && (
          // « Intervenir » posts a copilot turn too (playerIntervene → sendMessage).
          <WithCreditCost feature="copilot" className="shrink-0">
            <button
              type="button"
              onClick={handleIntervene}
              disabled={sending || draft.trim() === ''}
              aria-label={tx('shell.features.copilotCopilotWidget.intervenePauseStepByStepPlayback')}
              title={tx('shell.features.copilotCopilotWidget.pausesStepByStepPlaybackThen')}
              className="shrink-0 rounded-md border border-amber-500/60 px-2.5 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-40"
            >{tx('shell.features.copilotCopilotWidget.intervene')}</button>
          </WithCreditCost>
        )}
        <WithCreditCost feature="copilot" className="shrink-0">
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || draft.trim() === ''}
            aria-label={tx('shell.features.copilotCopilotWidget.sendTheMessage')}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          >{tx('shell.features.copilotCopilotWidget.send')}</button>
        </WithCreditCost>
      </div>
      )}
    </div>
  );

  const toastStack = toasts.length > 0 && (
    <div className="fixed bottom-24 right-5 z-[10002] flex w-72 flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 shadow-lg"
        >
          <span className="min-w-0 flex-1 break-words">{toast.message}</span>
          <button
            type="button"
            onClick={() => dismissToast(toast.id)}
            aria-label={tx('shell.features.copilotCopilotWidget.closeTheNotification')}
            className="shrink-0 text-slate-400 hover:text-slate-200"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );

  return createPortal(
    <>
      {showWindow ? panel : bubble}
      {toastStack}
    </>,
    document.body,
  );
}
